import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import useForm from 'rc-form-hooks';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { checkFirstTimeUseEmail, isEmailNeedRecaptchaV2, signUpWithoutPassword } from '@/api/customer';
import Button from '@/components/common/Button';
import GoogleRecaptchaV2 from '@/components/common/GoogleRecaptchaV2';
import { WeeeIcon } from '@/components/common/Icon';
import { hideLoading, showLoading } from '@/components/common/Indicator/pageIndicator';
import MaterialTextInput from '@/components/common/MaterialTextInput';
import PhoneTextInput from '@/components/common/MaterialTextInput/phone-text-input';
import { LoginSessionstoragekey } from '@/constants/account';
import { AUTH_SIGN } from '@/constants/auth_sign';
import Constants from '@/constants/const';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { LoginMethod } from '@/types/account.d';
import { afterUserLogin, afterUserRegisterLogin, afterUserSign } from '@/utils/common/pixel';
import { getInviterParams, getReferralIdFromCookie } from '@/utils/common/referral';
import { getCookie } from '@/utils/cookie';
import { updateFinger } from '@/utils/finger';
import md5 from '@/utils/md5';
import { formatAmericaPhoneToNumber } from '@/utils/phoneformat';
import { loadGoogloRecaptcha } from '@/utils/recaptcha';
import { PHONE_VALID_REG } from '@/constants/reg_rules';
import { getSafeRedirectUrl } from '@/utils/safeRedirect';
import { isMobileBrowser } from '@/utils/common/device';
import { checkValidityEmailStrict } from '@/utils/common/user';

/**
 * LoginTab 组件 - 登录标签页组件
 *
 * @example
 * // 使用默认内容容器选择器
 * <LoginTab />
 *
 * @example
 * // 使用自定义内容容器选择器
 * <LoginTab contentSelector=".custom-content-container" />
 *
 * @example
 * // 使用ID选择器
 * <LoginTab contentSelector="#login-content" />
 *
 * @example
 * // 使用类选择器
 * <LoginTab contentSelector=".login-form-container" />
 */

type Props = Partial<{
  onSuccess(any?: any, arg2?: any): void;
  onTabClick(any): void;
  isLoginSignupPopup?: boolean;
  gap?: number;
  contentSelector?: string;
  redirect?: string; // 自定义redirectUrl
  defaultActiveKey?: number;
}>;

const LoginTab = (props: Props) => {
  const {
    onSuccess,
    onTabClick,
    gap = 24,
    contentSelector = 'div[data-role="content"]',
    redirect,
    defaultActiveKey = 1
  } = props;
  const router = useRouter();
  const { query } = router;
  const redirectUrl = redirect ? redirect : getSafeRedirectUrl(router.query?.redirect_url as string);
  const { t, lang } = useTranslation('onboarding');
  const [activeKey, setActiveKey] = useState<number>(defaultActiveKey);
  const [activeWidth, setActiveWidth] = useState<number>(0);
  const [left, setLeft] = useState(0);
  /** 首次布局完成前关闭 transition，避免 defaultActiveKey≠1 时从 (0,0) 滑入的入场动画 */
  const [indicatorTransitionEnabled, setIndicatorTransitionEnabled] = useState(false);
  const [email, setEmail] = useState(null);
  const [emailFocus, setEmailFocus] = useState(false);
  const [phoneNum, setPhoneNum] = useState(null);
  const [phoneFocus, setPhoneFocus] = useState(false);
  const [errorRes, setErrorRes] = useState<string>('');
  const [recaptchaToken, setRecaptchaToken] = useState<string>('1');
  const { buttonClick } = useClickActionT2();
  const { getFieldDecorator, errors, setFieldsValue, getFieldValue, validateFields, resetFields } = useForm();

  const recaptchaRef = useRef(null);
  const isMobile = isMobileBrowser();

  const defaultTabs = [
    { title: t('email'), keyNum: 1 },
    { title: t('phone'), keyNum: 2 }
  ];

  const sendEpPartner = useMemo(() => {
    const epPartner = { [Constants.EP_PARTNER]: getCookie(Constants.EP_PARTNER) || '' };
    return epPartner;
  }, []);

  const sendParams = useMemo(() => {
    return getInviterParams();
  }, []);

  const handleRedirect = (url: string) => {
    if (isMobile) {
      router.push(url);
    } else {
      window.location.href = url;
    }
  };

  const getLoginSourceKey = () => {
    // 如果当前url中 /account/referral/landing 存在，则返回 referral
    // 否则返回 onboarding
    const pathname = window.location.pathname;
    if (pathname.includes('/account/referral/landing') || pathname.includes('/order/share/grocery/sign-up')) {
      return 'activity';
    }
    return 'newFlow';
  };

  const getBindConnectActiveKey = () => {
    let activeTabKey = 1;
    const urlPhoneQuery = router?.query?.phone ?? '';
    activeTabKey = urlPhoneQuery ? 2 : 1;
    return activeTabKey;
  };

  const handlePhoneNumber = input => {
    const usaNumber = formatAmericaPhoneToNumber(input);
    setPhoneNum(usaNumber);
  };

  const sendVerifyCode = e => {
    e?.preventDefault();
    validateFields(['phone'])
      .then(res => {
        const tab_nm = location.pathname.includes('login') ? 'login' : 'signup';
        if (location?.pathname?.includes('login') || location?.pathname?.includes('signup')) {
          buttonClick({
            co: {
              target_nm: tab_nm
            },
            ctx: {
              page_tab: 'phone'
            }
          });
        }
        const phone_number = getFieldValue('phone');
        const url = `/${lang}/account/onboarding/phone-verify?phone=${phone_number}&redirect_url=${encodeURIComponent(
          redirectUrl
        )}&tab=${tab_nm}`;
        handleRedirect(url);
      })
      .catch(() => {});
  };

  const validateEmail = async () => {
    validateFields(['email'])
      .then(async validRes => {
        showLoading();
        if (location?.pathname?.includes('login') || location?.pathname?.includes('signup')) {
          buttonClick({
            co: {
              target_nm: location.pathname.includes('login') ? 'login' : 'signup'
            },
            ctx: {
              page_tab: 'email'
            }
          });
        }
        const value = getFieldValue('email');
        const _recaptchaVisble = recaptchaRef.current?.visible;
        if (!_recaptchaVisble) {
          // 检测邮箱是否需要recapchat v2验证
          const checkRecaptchaRes = await isEmailNeedRecaptchaV2(
            { value },
            {
              headers: {
                source: getLoginSourceKey()
              }
            }
          );
          if (checkRecaptchaRes?.result && checkRecaptchaRes?.object?.check_result) {
            hideLoading();
            recaptchaRef.current?.show();
            return;
          }
        }
        const recaptchaV2Token = _recaptchaVisble ? await recaptchaRef.current?.getToken?.() : '';
        const checkRes = await checkFirstTimeUseEmail(
          {
            value: value,
            recaptcha_response: recaptchaV2Token
          },
          {
            headers: {
              source: getLoginSourceKey()
            }
          }
        );
        if (checkRes.result) {
          hideLoading();
          recaptchaV2Token && recaptchaRef.current?.hide();
          if (checkRes.object.is_first_time_user) {
            // pc 端暂时还是使用老流程
            if (isMobile) {
              // 存储 login key 到 sessionStorage, 用于后续验证码发送,这里跳过了 check-email 流程
              window.sessionStorage?.setItem?.(LoginSessionstoragekey.login_key, checkRes?.object?.code as string);
              handleRedirect(
                `/${lang}/account/verify?email=${encodeURIComponent(value)}&redirect_url=${encodeURIComponent(
                  redirectUrl
                )}`
              );
            } else {
              handleRedirect(
                `/${lang}/account/check-email?email=${encodeURIComponent(value)}&redirect_url=${encodeURIComponent(
                  redirectUrl
                )}`
              );
            }
          } else if (checkRes.object.check_email) {
            // 全新用户
            localStorage.setItem(Constants.POPUP_CREATE_ACCOUNT_EMAIL, value);
            try {
              let extraParams = {};
              let adsParams = JSON.parse(getCookie('ads') || '{}');
              extraParams = {
                ...extraParams,
                ...adsParams
              };
            } catch (error) {}
            try {
              let device_id = getCookie(Constants.B_COOKIE);
              let timeStamp = Math.floor(new Date().getTime() / 1000);
              let sign = md5(`${value}${device_id}${timeStamp}${AUTH_SIGN}`);

              await updateFinger();
              const res = await signUpWithoutPassword(
                {
                  email: value,
                  from: 'first_time_use',
                  timestamp: timeStamp,
                  source: getLoginSourceKey(),
                  referral_id: getReferralIdFromCookie(),
                  recaptcha_response: recaptchaToken,
                  ...sendEpPartner,
                  ...sendParams
                },
                {
                  headers: {
                    sign
                  }
                }
              );
              buttonClick({
                co: {
                  target_nm: location.pathname.includes('login') ? 'login' : 'signup',
                  click_type: 'complete'
                },
                ctx: {
                  page_tab: 'email',
                  result: res
                }
              });
              if (res.result) {
                afterUserRegisterLogin(res?.object?.token, res?.object?.language);
                if (res?.object?.is_sign_up) {
                  afterUserSign('email');
                } else {
                  afterUserLogin('email');
                }
                if (res?.object?.is_edu_email_check) {
                  handleRedirect(
                    `/${lang}/student-discount/email-send?email=${encodeURIComponent(
                      value
                    )}&redirect_url=${encodeURIComponent(redirectUrl)}`
                  );
                  return;
                } else {
                  onSuccess({ email: email }, res?.object);
                }
              } else {
                // CSM10026是登录流程的error code, 应该不会返回，是原逻辑暂时保留
                if (res?.message_id === 'CSM10026' || res?.message_id === 'CSM10031') {
                  let addRedirectUrl = '';
                  if (!!redirectUrl) {
                    addRedirectUrl = `&redirect_url=${encodeURIComponent(redirectUrl)}`;
                  }
                  window.sessionStorage.setItem(LoginSessionstoragekey.login_key, res?.message as string);
                  let _url = `/${lang}/account/verify?email=${encodeURIComponent(email)}${addRedirectUrl}`;
                  if (res?.message_id === 'CSM10031') {
                    _url = `${_url}&type=signup`;
                  }
                  handleRedirect(_url);

                  return;
                }
                setErrorRes(res?.message);
              }
            } catch (error) {
              setErrorRes(checkRes?.message);
            }
          } else {
            // 已经通过账号密码注册过了
            afterUserLogin('email');
            handleRedirect(
              `/${lang}/account/onboarding/login-with-password?email=${encodeURIComponent(
                value
              )}&redirect_url=${encodeURIComponent(redirectUrl)}`
            );
          }
        } else {
          if (checkRes.message_id === 'CSM30018') {
            recaptchaRef.current?.reset?.();
          }
          hideLoading();
          setErrorRes(checkRes?.message);
        }
      })
      .catch(() => {});
  };

  const handleNext = async e => {
    e.stopPropagation();
    await updateFinger();
    if (activeKey === 1) {
      validateEmail();
    }
    if (activeKey === 2) {
      sendVerifyCode(e);
    }
  };

  useLayoutEffect(() => {
    const domRec = document.querySelector('span[data-role="tag-item-active"]')?.getBoundingClientRect();
    const contentDom = document.querySelector(contentSelector)?.getBoundingClientRect();
    if (!domRec) {
      return;
    }
    const { width, left: itemLeft } = domRec;
    setActiveWidth(width);
    setLeft(itemLeft - (contentDom?.left ?? 0) - gap);
    // 在浏览器绘制到最终位置后再开启 transition，后续切换 tab 仍有滑动效果
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setIndicatorTransitionEnabled(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeKey, gap, contentSelector]);

  useEffect(() => {
    const forceMethod = (query?.force_method as string) || '';
    const isPhone = forceMethod === LoginMethod.PHONE;
    const forceKey = isPhone ? 2 : 1;
    const key = forceKey || getBindConnectActiveKey();
    // 邀请好友tab1是邮箱 tab2是手机。微信登陆后bind-connect页面返回 phone=136 或者email=11@1.com
    const activeKey = Object.keys(router?.query).includes('tab') ? +router?.query?.tab : key;
    // 如果 query 上没有 tab,force_method,phone 则使用默认的 defaultActiveKey
    const useQueryKey = query?.force_method || query?.phone || query?.tab;
    setActiveKey(useQueryKey ? activeKey : defaultActiveKey);
  }, []);

  useEffect(() => {
    //若query中有continue_with_e参数，并且是一个合法的邮箱，则预设到输入框内
    const continueWithEmail = decodeURIComponent((router?.query?.continue_with_e as string) ?? '');
    if (continueWithEmail && checkValidityEmailStrict(continueWithEmail)) {
      setTimeout(() => {
        setEmail(continueWithEmail);
        setFieldsValue({ email: continueWithEmail });
      }, 300);
    }
  }, [router]);

  const getGoogleRecaptchaToken = async () => {
    const token = await loadGoogloRecaptcha();
    setRecaptchaToken(token);
  };
  useEffect(() => {
    getGoogleRecaptchaToken();
  }, []);

  const renderNext = () => {
    return (
      <Button
        data-testid="btn-next"
        type="primary"
        onClick={handleNext}
        className="h-[34px] text-btn-primary-fg-default enki-body-sm-medium p-[6px] text-center min-w-[65px]">
        {t('next')}
      </Button>
    );
  };

  const renderTab = () => {
    return (
      <div
        data-testid="wid-login-tab-item-container"
        className="relative inline-flex items-center gap-[13px] border-b-[2px] border-solid border-surface-100-hairline">
        <div
          style={{
            width: activeWidth,
            transform: `translate3d(${left}px, 0px, 0px)`,
            transition: indicatorTransitionEnabled ? 'all .2s' : 'none'
          }}
          className={twMerge('absolute -bottom-[2px] h-[2px] bg-surface-600-bg')}></div>
        {defaultTabs.map((tag, index) => {
          return (
            <span
              data-testid={`wid-login-tab-item`}
              data-actived={tag.keyNum === activeKey}
              data-key={tag.keyNum}
              key={index}
              data-role={tag.keyNum === activeKey ? 'tag-item-active' : 'tag-item'}
              className={twMerge(
                'px-2 pb-2 enki-body-base-medium text-surface-100-fg-minor cursor-pointer',
                activeKey === tag.keyNum && 'text-surface-100-fg-default'
              )}
              onClick={e => {
                setActiveKey(tag.keyNum);
                onTabClick?.(tag);
                // setEmailFocus(tag.keyNum === 1);
                // setPhoneFocus(tag.keyNum === 2);
                if (tag.keyNum === 2 && recaptchaRef.current?.visible) {
                  recaptchaRef.current?.hide();
                }
              }}>
              {tag.title}
            </span>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="pt-5">
        {activeKey === 1 && (
          <>
            {getFieldDecorator('email', {
              rules: [{ required: true, type: 'email', message: t('email_error_msg') }],
              initialValue: ''
            })(
              <div data-testid="wid-email-input">
                <MaterialTextInput
                  id="email"
                  value={email}
                  type="email"
                  hasError={!!errors?.email}
                  placeholder={t('enter_email')}
                  // autoFocus={emailFocus}
                  autoComplete="email"
                  dataTestId="wid-email-input"
                  prefix={
                    <WeeeIcon
                      data-testid="wid-email-input-prefix"
                      className={twMerge(
                        '!text-[20px] text-surface-100-fg-minor',
                        emailFocus && 'text-surface-100-fg-default'
                      )}
                      type="iconme_select"
                    />
                  }
                  suffix={renderNext()}
                  onFocus={e => {
                    // setEmailFocus(true);
                    if (!e) {
                      resetFields(['email']);
                    }
                  }}
                  onPressEnter={validateEmail}
                  onBlur={() => {
                    // setEmailFocus(false);
                  }}
                  onChange={val => {
                    setErrorRes('');
                    setEmail(val);
                    if (recaptchaRef.current?.visible) {
                      recaptchaRef.current?.hide();
                    }
                  }}
                />
              </div>
            )}
            <GoogleRecaptchaV2 id="account-login-recaptcha" ref={recaptchaRef} responseCallback={validateEmail} />
          </>
        )}
        {activeKey === 2 && (
          <>
            {getFieldDecorator('phone', {
              rules: [{ required: true, len: 10, pattern: PHONE_VALID_REG, message: t('address_err_msg_phone') }],
              initialValue: ''
            })(
              <PhoneTextInput
                id="phone"
                type="tel"
                value={phoneNum}
                autoComplete="phone"
                // autoFocus={phoneFocus}
                hasError={!!errors?.phone}
                placeholder={t('enter_phone_num')}
                dataTestId="wid-phone-input"
                prefix={
                  <div
                    data-testid="wid-phone-input-prefix"
                    className={twMerge(
                      'enki-body-sm-medium text-surface-100-fg-minor pr-[15px] py-[5px] border-r border-solid border-surface-100-hairline',
                      phoneFocus && 'text-surface-100-fg-default'
                    )}>
                    {'+1'}
                  </div>
                }
                suffix={renderNext()}
                onFocus={e => {
                  // setPhoneFocus(true);
                  if (!e) {
                    resetFields(['phone']);
                  }
                }}
                onPressEnter={sendVerifyCode}
                onBlur={() => {
                  // setPhoneFocus(false);
                }}
                onChange={val => {
                  setErrorRes('');
                  handlePhoneNumber(val);
                }}
              />
            )}
          </>
        )}
        {(errorRes && (
          <span data-testid="wid-login-tab-error-res" className="enki-body-3xs text-input-100-fg-critical block mt-2">
            {errorRes}
          </span>
        )) ||
          (errors?.email && activeKey === 1 && (
            <span data-testid="wid-login-tab-error-res" className="enki-body-3xs text-input-100-fg-critical block mt-2">
              {errors.email?.find(i => i?.field === 'email')?.message}
            </span>
          )) ||
          (errors?.phone && activeKey === 2 && (
            <span data-testid="wid-login-tab-error-res" className="enki-body-3xs text-input-100-fg-critical block mt-2">
              {errors?.phone?.find(i => i?.field === 'phone')?.message}
            </span>
          ))}
      </div>
    );
  };

  return (
    <div data-testid="wid-login-tab" className="py-5">
      {renderTab()}
      {renderContent()}
    </div>
  );
};

export default LoginTab;
